import React from 'react';
import {useQueryClient} from 'react-query';
import {fetchTask, useCompleteTask} from '@rocket-ui/lk-api';
import {useDeclineTaskDrawer} from 'features/draft-task-actions/model/useDeclineTaskDrawer';
import {usePollTaskOnAcceptanceSign} from './usePollTaskOnAcceptanceSign';
import {usePollTaskOnSign} from './usePollTaskOnSign';
import {refreshOpenedTask} from './refreshOpenedTask';
import {refreshOrdersTableFirstPage} from './refreshOrdersTableFirstPage';
import {isFreeSmzReceiptError} from './manualFnsReceipt';
import {useRequiredSelfEmploymentGuard} from './useRequiredSelfEmploymentGuard';
import type {
	CreateReceiptController,
	EmploymentMismatchController,
	OrderActionDescriptor,
	OrderActionsView,
	SignAcceptanceController,
	TaskForOrderActions,
} from './types';

function openInNewTab(uri: string) {
	window.open(uri, '_blank', 'noopener,noreferrer');
}

export function useOrderActions(task: TaskForOrderActions, view: OrderActionsView = 'order') {
	const queryClient = useQueryClient();
	const {mutateAsync: completeTask, isLoading: isCompleting} = useCompleteTask();
	const declineDrawer = useDeclineTaskDrawer(task.id ?? 0);
	const {startPolling: startContractPolling} = usePollTaskOnSign();
	const {startPolling: startAcceptancePolling} = usePollTaskOnAcceptanceSign();
	const employmentGuard = useRequiredSelfEmploymentGuard(task.requiredSelfemploymentProvider);
	const isDraftInTable = view === 'table' && task.status === 'draft';
	const isInworkAcceptanceRequired = task.status === 'inwork' && task.acceptanceRequired === true;
	const isPaymentError = task.status === 'error';
	const isCompleted = task.status === 'completed';
	const isCanceled = task.status === 'canceled';
	const isPaying = task.status === 'paying';
	const canCreateNewReceipt =
		isPaying &&
		task.requiredSelfemploymentProvider === 'free_smz' &&
		task.manualFnsReceipt?.required === true &&
		task.manualFnsReceipt.status === 'new';
	const canRetryRejectedReceipt = view === 'table' && isFreeSmzReceiptError(task);
	const canCreateReceipt = canCreateNewReceipt || canRetryRejectedReceipt;
	const hasNoActions =
		!canRetryRejectedReceipt && (isPaymentError || isCompleted || isCanceled || (isPaying && !canCreateReceipt));

	const [isSignAcceptanceOpen, setIsSignAcceptanceOpen] = React.useState(false);
	const [isCreateReceiptOpen, setIsCreateReceiptOpen] = React.useState(false);

	const openSignAcceptance = React.useCallback(() => setIsSignAcceptanceOpen(true), []);
	const closeSignAcceptance = React.useCallback(() => setIsSignAcceptanceOpen(false), []);
	const openCreateReceipt = React.useCallback(() => {
		if (task.id == null) return;

		void employmentGuard.run({
			run: () => setIsCreateReceiptOpen(true),
		});
	}, [employmentGuard.run, task.id]);
	const closeCreateReceipt = React.useCallback(() => {
		setIsCreateReceiptOpen(false);

		if (task.id == null) return;

		void refreshOpenedTask(queryClient, task.id, view).catch(() => {
			// Закрытие модалки не зависит от обновления задачи.
		});
	}, [queryClient, task.id, view]);

	const handleAccept = React.useCallback(() => {
		if (task.status !== 'draft' || !task.contractCertificateUri) return;

		const certificateUri = task.contractCertificateUri;

		void employmentGuard.run({
			openUrl: certificateUri,
			run: () => {
				if (task.id != null) {
					startContractPolling(task.id, view, task.status);
				}
			},
		});
	}, [employmentGuard.run, startContractPolling, task.contractCertificateUri, task.id, task.status, view]);

	const handleDecline = React.useCallback(() => {
		declineDrawer.open();
	}, [declineDrawer]);

	const handleComplete = React.useCallback(() => {
		if (task.id == null) return;

		const taskId = task.id;

		void employmentGuard.run({
			run: async () => {
				await completeTask({id: String(taskId)});

				try {
					const {data: updatedTask} = await fetchTask({id: String(taskId)});
					queryClient.setQueryData(['task', taskId], updatedTask);
					queryClient.setQueryData(['task', String(taskId)], updatedTask);
				} finally {
					await refreshOrdersTableFirstPage(queryClient);
				}
			},
		});
	}, [completeTask, employmentGuard.run, queryClient, task.id]);

	const handleOpenAcceptance = React.useCallback(() => {
		if (!task.acceptanceUri) return;
		openInNewTab(task.acceptanceUri);
	}, [task.acceptanceUri]);

	const handleSignAcceptance = React.useCallback(() => {
		if (!task.acceptanceCertificateUri) return;

		const certificateUri = task.acceptanceCertificateUri;

		void employmentGuard.run({
			openUrl: certificateUri,
			run: () => {
				closeSignAcceptance();

				if (task.id != null) {
					startAcceptancePolling(task.id, view, task.status);
				}
			},
		});
	}, [
		closeSignAcceptance,
		employmentGuard.run,
		startAcceptancePolling,
		task.acceptanceCertificateUri,
		task.id,
		task.status,
		view,
	]);

	const handleViewReceipt = React.useCallback(() => {
		const uri = task.receiptUri || task.offlineReceiptUri;
		if (!uri) return;
		openInNewTab(uri);
	}, [task.offlineReceiptUri, task.receiptUri]);

	const handleCreateReceipt = openCreateReceipt;

	const actions: OrderActionDescriptor[] = React.useMemo(() => {
		if (hasNoActions || task.status === 'accepting') return [];

		const list: OrderActionDescriptor[] = [];

		list.push({
			id: 'accept',
			label: 'Подписать',
			variant: 'primary',
			visible: task.status === 'draft' && Boolean(task.contractCertificateUri),
			onSelect: handleAccept,
		});

		list.push({
			id: 'complete',
			label: 'Выполнить',
			variant: 'primary',
			visible: task.status === 'inwork',
			disabled: isInworkAcceptanceRequired ? !task.acceptanceCertificateUri : isCompleting,
			onSelect: isInworkAcceptanceRequired ? handleSignAcceptance : handleComplete,
		});

		list.push({
			id: 'decline',
			label: isInworkAcceptanceRequired ? 'Отменить' : 'Отклонить',
			variant: 'secondary',
			visible: task.status === 'draft' && view !== 'table' && Boolean(task.cancellable),
			onSelect: handleDecline,
		});

		list.push({
			id: 'viewReceipt',
			label: 'Посмотреть чек',
			visible:
				Boolean(task.receiptUri || task.offlineReceiptUri) &&
				(view === 'table' ? task.status === 'paid' : !isDraftInTable) &&
				!isInworkAcceptanceRequired,
			onSelect: handleViewReceipt,
		});

		list.push({
			id: 'createReceipt',
			label: 'Создать чек',
			variant: 'primary',
			visible: canCreateReceipt,
			onSelect: handleCreateReceipt,
		});

		return list.filter((a) => a.visible);
	}, [
		canCreateReceipt,
		handleAccept,
		handleComplete,
		handleCreateReceipt,
		handleDecline,
		handleSignAcceptance,
		handleViewReceipt,
		isCompleting,
		isDraftInTable,
		isInworkAcceptanceRequired,
		hasNoActions,
		task.acceptanceCertificateUri,
		task.cancellable,
		task.contractCertificateUri,
		task.offlineReceiptUri,
		task.receiptUri,
		task.status,
		view,
	]);

	return {
		actions,
		signAcceptance: {
			isOpen: isSignAcceptanceOpen,
			open: openSignAcceptance,
			close: closeSignAcceptance,
			openAcceptance: handleOpenAcceptance,
			signAcceptance: handleSignAcceptance,
		} satisfies SignAcceptanceController,
		createReceipt: {
			isOpen: isCreateReceiptOpen,
			open: openCreateReceipt,
			close: closeCreateReceipt,
			taskId: task.id ?? null,
		} satisfies CreateReceiptController,
		declineDrawer,
		employmentMismatch: {
			isOpen: employmentGuard.isOpen,
			close: employmentGuard.close,
		} satisfies EmploymentMismatchController,
	};
}
